import { useState, useEffect, useMemo, useRef } from 'react'
import {
  User, Building2, Bell, Palette, Server,
  Globe, Shield, Database, Eye, EyeOff, Loader2,
  Settings2, RefreshCw, Eraser, PlayCircle, FolderOpen, Search,
  ChevronRight, ChevronDown, Code, Cloud, Monitor, FileText,
  Lock, Unlock, AlertCircle, CheckCircle2, Keyboard, Info,
  GitBranch, HelpCircle, ExternalLink, Coffee, Wrench, Download,
  Mail, Send, Copy, X, Clock, UserPlus, Plus, Layers
} from 'lucide-react'
import Modal, { FormField, FormInput, FormSelect, FormTextarea, FormActions, Button } from './Modal'
import { useSettings } from '../../hooks'
import { isElectron, getAPIBaseUrl } from '../../environment'
import TargetProfilesModal from '../landing/TargetProfilesModal'
import './SettingsModal.css'

// Hierarchical settings structure (like JetBrains)
const SETTINGS_TREE = [
  {
    id: 'appearance-behavior',
    label: 'Appearance & Behavior',
    icon: Palette,
    children: [
      { id: 'appearance', label: 'Appearance', icon: Monitor },
      { id: 'notifications', label: 'Notifications', icon: Bell, disabled: true },
      { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: Keyboard, disabled: true },
      { id: 'tooltips', label: 'Tooltips', icon: Info, disabled: true },
    ]
  },
  {
    id: 'system-settings',
    label: 'System Settings',
    icon: Settings2,
    children: [
      { id: 'files', label: 'File Storage', icon: FolderOpen, disabled: true },
      // { id: 'connections', label: 'Connections', icon: Server }, // Disabled - future: dev tools & CI/CD integrations
      { id: 'api', label: 'API Layer', icon: Globe, disabled: true },
      { id: 'database', label: 'Database', icon: Database, disabled: true },
    ]
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: Wrench,
    children: [
      { id: 'tools-downloads', label: 'Downloads', icon: Download, disabled: true },
    ]
  },
  {
    id: 'deploy-publish',
    label: 'Deploy / Publish',
    icon: Layers,
    children: [
      { id: 'target-profiles', label: 'Target Profiles', icon: Layers },
    ]
  },
  {
    id: 'editor',
    label: 'Editor',
    icon: Code,
    children: [
      { id: 'editor-general', label: 'General', icon: FileText, disabled: true },
    ]
  },
  {
    id: 'ai-keys',
    label: 'AWS Credentials',
    icon: Cloud,
    children: [
      { id: 'aws', label: 'AWS Bedrock', icon: Cloud, disabled: false },
    ]
  },
  {
    id: 'account',
    label: 'Account',
    icon: Building2,
    children: [
      { id: 'users', label: 'Users', icon: User, disabled: true },
      { id: 'invite-users', label: 'Invite Users', icon: Mail, disabled: true },
      { id: 'request-account', label: 'Request Account', icon: UserPlus, disabled: true },
      { id: 'default-account', label: 'Default Account', icon: Building2, disabled: true },
    ]
  },
  {
    id: 'about',
    label: 'About',
    icon: Info,
    children: [
      { id: 'about-app', label: 'RecodeIT', icon: Info, disabled: true },
      { id: 'help', label: 'Help & Support', icon: HelpCircle, disabled: true },
    ]
  },
]

const DATABASE_TYPES = [
  { value: 'sqlite', label: 'SQLite (Local)' },
  { value: 'postgres', label: 'PostgreSQL' },
  { value: 'rds', label: 'AWS RDS' },
]

const THEMES = [
  { value: 'dark', label: 'Dark', description: 'Default dark theme' },
  { value: 'light', label: 'Light', description: 'Light theme for bright environments' },
  { value: 'system', label: 'System', description: 'Follow system preference' },
  { value: 'midnight', label: 'Midnight Blue', description: 'Deep blue dark theme' },
  { value: 'nord', label: 'Nord', description: 'Arctic, north-bluish color palette' },
  { value: 'high-contrast', label: 'High Contrast', description: 'Maximum readability' },
  // Custom Themes
  { value: 'scoutitai-portal', label: 'ScoutITAI Portal', description: 'Light teal theme inspired by Scout-itAI', isCustom: true },
]

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
]

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
]

// Keyboard Shortcuts
const KEYBOARD_SHORTCUTS = [
  { category: 'General', shortcuts: [
    { keys: '⌘ + S', action: 'Save', description: 'Save current file' },
    { keys: '⌘ + ,', action: 'Settings', description: 'Open settings' },
    { keys: '⌘ + P', action: 'Quick Open', description: 'Open file picker' },
    { keys: '⌘ + ⇧ + P', action: 'Command Palette', description: 'Open command palette' },
  ]},
  { category: 'Editor', shortcuts: [
    { keys: '⌘ + Z', action: 'Undo', description: 'Undo last action' },
    { keys: '⌘ + ⇧ + Z', action: 'Redo', description: 'Redo last action' },
    { keys: '⌘ + F', action: 'Find', description: 'Find in file' },
    { keys: '⌘ + H', action: 'Replace', description: 'Find and replace' },
    { keys: '⌘ + /', action: 'Toggle Comment', description: 'Comment/uncomment line' },
  ]},
  { category: 'Transformation', shortcuts: [
    { keys: '⌘ + ⇧ + T', action: 'Transform', description: 'Transform selected code' },
    { keys: '⌘ + ⇧ + A', action: 'AI Assist', description: 'Open AI assistant' },
    { keys: '⌘ + ⇧ + E', action: 'Explain', description: 'Explain selected code' },
  ]},
  { category: 'Navigation', shortcuts: [
    { keys: '⌘ + G', action: 'Go to Line', description: 'Jump to line number' },
    { keys: '⌘ + B', action: 'Go to Definition', description: 'Jump to definition' },
    { keys: '⌘ + [', action: 'Back', description: 'Navigate back' },
    { keys: '⌘ + ]', action: 'Forward', description: 'Navigate forward' },
  ]},
]

// Hardcoded admin credentials (will move to SQLite later)
const ADMIN_CREDENTIALS = {
  username: 'admin',
  password: 'admin'
}

function SettingsModal({ isOpen, onClose, onProfileSaved }) {
  const [activeSection, setActiveSection] = useState('appearance')
  const [expandedCategories, setExpandedCategories] = useState(['appearance-behavior'])
  const [searchQuery, setSearchQuery] = useState('')
  const [showSecrets, setShowSecrets] = useState({})
  const [systemStatus, setSystemStatus] = useState({ loading: false, message: '', success: null })
  const [showTargetProfilesModal, setShowTargetProfilesModal] = useState(false)

  // User management state
  const [accountUsers, setAccountUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [editingUser, setEditingUser] = useState(null)
  const [selectedRole, setSelectedRole] = useState('')
  const [savingRole, setSavingRole] = useState(false)

  // Invitation state
  const [invitations, setInvitations] = useState([])
  const [loadingInvites, setLoadingInvites] = useState(true)
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' })
  const [sendingInvite, setSendingInvite] = useState(false)
  const [inviteLink, setInviteLink] = useState(null)

  // User's accounts (memberships) state
  const [myAccounts, setMyAccounts] = useState([])
  const [loadingMyAccounts, setLoadingMyAccounts] = useState(true)
  const [defaultAccountId, setDefaultAccountId] = useState(null)

  // Request account state
  const [accountSearchQuery, setAccountSearchQuery] = useState('')
  const [accountSearchResults, setAccountSearchResults] = useState([])
  const [searchingAccounts, setSearchingAccounts] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [requestMessage, setRequestMessage] = useState('')
  const [createForm, setCreateForm] = useState({ name: '', description: '', s3Bucket: '', s3Region: 'us-east-1' })
  const [accountRequests, setAccountRequests] = useState([])
  const [loadingRequests, setLoadingRequests] = useState(true)
  const [submittingRequest, setSubmittingRequest] = useState(false)
  const [creatingAccount, setCreatingAccount] = useState(false)

  // Admin authentication state
  const [isAdminUnlocked, setIsAdminUnlocked] = useState(false)
  const [showLoginPanel, setShowLoginPanel] = useState(false)
  const [adminUsername, setAdminUsername] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginSuccess, setLoginSuccess] = useState(false)
  const [showAdminPassword, setShowAdminPassword] = useState(false)
  const loginPanelRef = useRef(null)
  const usernameInputRef = useRef(null)

  // Use the settings hook for state management
  const {
    profile,
    preferences,
    notifications,
    awsCredentials,
    aiProviders,
    loading,
    saving,
    error,
    updateProfile,
    updatePreferences,
    updateNotifications,
    updateAWSCredentials,
    updateAIProvider,
  } = useSettings()

  // Local form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    defaultAccountId: 1,
    awsCredentials: [{
      id: 1,
      profile_name: 'default',
      aws_access_key_id: '',
      aws_secret_access_key: '',
      region: 'us-east-1',
      account_id: '',
      s3_bucket: '',
    }],
    emailEnabled: true,
    emailJobComplete: true,
    emailJobFailed: true,
    emailWeeklySummary: false,
    notificationEmail: '',
    slackEnabled: false,
    slackWebhookUrl: '',
    slackJobComplete: true,
    slackJobFailed: true,
    theme: 'dark',
    fontSize: 14,
    editorFont: 'SF Mono',
    showMinimap: true,
    showLineNumbers: true,
    autoSave: true,
    tooltipLevel: 'simple',
    mainframeHost: '',
    gitProvider: 'github',
    apiBaseUrl: 'http://localhost:8000',
    databaseType: 'sqlite',
    sqlitePath: './data/modernizeit.db',
    sqlHost: '',
    sqlPort: '5432',
    sqlDatabase: '',
    sqlUsername: '',
    sqlPassword: '',
    sqlSslEnabled: true,
    localEnabled: true,
    localBasePath: '',
    s3Enabled: false,
    s3Bucket: '',
    s3Prefix: '',
    s3Region: 'us-east-1',
  })

  // Load account users when navigating to Users section
  useEffect(() => {
    const loadUsers = async () => {
      if (activeSection !== 'users') return

      const currentUserStr = localStorage.getItem('modernizeit_user')
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
      if (!currentUser?.account_id) return

      setLoadingUsers(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        const response = await fetch(`${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setAccountUsers(data.users || [])
        }
      } catch (err) {
        console.error('Failed to load account users:', err)
      } finally {
        setLoadingUsers(false)
      }
    }

    loadUsers()
  }, [activeSection])

  // Load invitations when navigating to Invite Users section
  useEffect(() => {
    const loadInvitations = async () => {
      if (activeSection !== 'invite-users') return

      const currentUserStr = localStorage.getItem('modernizeit_user')
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
      if (!currentUser?.account_id) return

      setLoadingInvites(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        const response = await fetch(`${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setInvitations(data.invites || [])
        }
      } catch (err) {
        console.error('Failed to load invitations:', err)
      } finally {
        setLoadingInvites(false)
      }
    }

    loadInvitations()
  }, [activeSection])

  // Load user's accounts (memberships) when navigating to default-account section
  useEffect(() => {
    const loadMyAccounts = async () => {
      if (activeSection !== 'default-account') return

      setLoadingMyAccounts(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        const response = await fetch(`${getAPIBaseUrl()}/auth/me/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setMyAccounts(data.accounts || [])

          // Load default account from localStorage
          const savedDefault = localStorage.getItem('modernizeit_default_account_id')
          if (savedDefault) {
            setDefaultAccountId(savedDefault)
          } else {
            // Default to current account if not set
            const currentUserStr = localStorage.getItem('modernizeit_user')
            const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
            if (currentUser?.account_id) {
              setDefaultAccountId(currentUser.account_id)
            }
          }
        }
      } catch (err) {
        console.error('Failed to load accounts:', err)
      } finally {
        setLoadingMyAccounts(false)
      }
    }

    loadMyAccounts()
  }, [activeSection])

  // Load account requests when navigating to request-account section
  useEffect(() => {
    const loadAccountRequests = async () => {
      if (activeSection !== 'request-account') return

      setLoadingRequests(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        const response = await fetch(`${getAPIBaseUrl()}/auth/me/requests`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setAccountRequests(data.requests || [])
        }
      } catch (err) {
        console.error('Failed to load account requests:', err)
      } finally {
        setLoadingRequests(false)
      }
    }

    loadAccountRequests()
  }, [activeSection])

  // Search for accounts as user types
  useEffect(() => {
    const searchAccounts = async () => {
      if (activeSection !== 'request-account') return
      if (!accountSearchQuery.trim()) {
        setAccountSearchResults([])
        return
      }

      setSearchingAccounts(true)
      try {
        const token = localStorage.getItem('modernizeit_token')
        const response = await fetch(
          `${getAPIBaseUrl()}/accounts/search?query=${encodeURIComponent(accountSearchQuery)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.ok) {
          const data = await response.json()
          setAccountSearchResults(data.accounts || [])
        }
      } catch (err) {
        console.error('Failed to search accounts:', err)
      } finally {
        setSearchingAccounts(false)
      }
    }

    // Debounce search
    const timeoutId = setTimeout(searchAccounts, 300)
    return () => clearTimeout(timeoutId)
  }, [accountSearchQuery, activeSection])

  // Sync form data from hook state
  useEffect(() => {
    if (!loading) {
      setFormData(prev => ({
        ...prev,
        firstName: profile.name?.split(' ')[0] || '',
        lastName: profile.name?.split(' ').slice(1).join(' ') || '',
        displayName: profile.name || '',
        email: profile.email || '',
        language: profile.language || 'en',
        dateFormat: profile.dateFormat || 'MM/DD/YYYY',
        theme: preferences.theme || 'dark',
        fontSize: preferences.fontSize || 14,
        editorFont: preferences.editorSettings?.fontFamily || 'JetBrains Mono',
        showMinimap: preferences.editorSettings?.minimap ?? true,
        showLineNumbers: preferences.editorSettings?.lineNumbers ?? true,
        autoSave: preferences.editorSettings?.autoSave ?? true,
        tooltipLevel: preferences.tooltipLevel || 'simple',
        // POC: awsCredentials in formData carries only safe metadata — no secrets
        awsCredentials: [{
          configured:        awsCredentials.configured ?? false,
          region:            awsCredentials.region || 'us-east-1',
          accessKeyIdMasked: awsCredentials.accessKeyIdMasked || null,
          managed:           true,
        }],
        emailEnabled: notifications.emailNotifications ?? true,
        emailJobComplete: notifications.jobCompletionAlerts ?? true,
        emailJobFailed: notifications.errorAlerts ?? true,
        slackEnabled: notifications.slackNotifications ?? false,
      }))
    }
  }, [loading, profile, preferences, notifications, awsCredentials, aiProviders])

  // Load file storage settings via IPC (Electron) or defaults
  useEffect(() => {
    const loadFileStorage = async () => {
      try {
        if (isElectron() && window.electronAPI?.getFileStorageSettings) {
          const data = await window.electronAPI.getFileStorageSettings()
          setFormData(prev => ({
            ...prev,
            localEnabled: data?.localEnabled ?? true,
            localBasePath: data?.localBasePath || '',
            s3Enabled: data?.s3Enabled ?? true,
            s3Bucket: data?.s3Bucket || '',
            s3Prefix: data?.s3Prefix || '',
            s3Region: data?.s3Region || 'us-east-1',
          }))
        } else {
          // Web Portal: S3 only, local disabled
          setFormData(prev => ({
            ...prev,
            localEnabled: false,
            localBasePath: '',
            s3Enabled: true,
            s3Bucket: prev.s3Bucket || '',
            s3Prefix: prev.s3Prefix || '',
            s3Region: prev.s3Region || 'us-east-1',
          }))
        }
      } catch (error) {
        console.error('Failed to load file storage settings:', error)
      }
    }
    loadFileStorage()
  }, [])

  // Focus username input when login panel opens
  useEffect(() => {
    if (showLoginPanel && usernameInputRef.current) {
      setTimeout(() => usernameInputRef.current?.focus(), 100)
    }
  }, [showLoginPanel])

  // Close login panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loginPanelRef.current && !loginPanelRef.current.contains(e.target)) {
        setShowLoginPanel(false)
        setLoginError('')
      }
    }
    if (showLoginPanel) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showLoginPanel])

  // Handle admin login
  const handleAdminLogin = (e) => {
    e.preventDefault()
    setLoginError('')

    if (adminUsername === ADMIN_CREDENTIALS.username && adminPassword === ADMIN_CREDENTIALS.password) {
      setLoginSuccess(true)
      setTimeout(() => {
        setIsAdminUnlocked(true)
        setShowLoginPanel(false)
        setLoginSuccess(false)
        setAdminUsername('')
        setAdminPassword('')
      }, 800)
    } else {
      setLoginError('Invalid credentials')
      // Shake animation handled by CSS
    }
  }

  // Handle admin logout
  const handleAdminLogout = () => {
    setIsAdminUnlocked(false)
    setAdminUsername('')
    setAdminPassword('')
  }

  // Toggle lock panel
  const handleLockClick = () => {
    if (isAdminUnlocked) {
      handleAdminLogout()
    } else {
      setShowLoginPanel(!showLoginPanel)
      setLoginError('')
    }
  }

  // Mock accounts list
  const [accounts] = useState([
    { id: 1, account_id: '376129851858', account_name: 'AWS Production', is_default: 1 },
    { id: 2, account_id: 'ACME123', account_name: 'ACME Industries', is_default: 0 },
    { id: 3, account_id: 'DEMO', account_name: 'Demo Account', is_default: 0 },
  ])

  // Get breadcrumb path
  const breadcrumb = useMemo(() => {
    for (const category of SETTINGS_TREE) {
      if (category.id === activeSection) {
        return [category.label]
      }
      const child = category.children?.find(c => c.id === activeSection)
      if (child) {
        return [category.label, child.label]
      }
    }
    return []
  }, [activeSection])

  // Filter tree based on search
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return SETTINGS_TREE

    const query = searchQuery.toLowerCase()
    return SETTINGS_TREE.map(category => {
      const matchingChildren = category.children?.filter(child =>
        child.label.toLowerCase().includes(query)
      ) || []

      if (category.label.toLowerCase().includes(query) || matchingChildren.length > 0) {
        return { ...category, children: matchingChildren.length > 0 ? matchingChildren : category.children }
      }
      return null
    }).filter(Boolean)
  }, [searchQuery])

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const updateSetting = async (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }))

    // Save theme immediately when changed (don't wait for Apply/OK)
    if (key === 'theme') {
      console.log('[Settings] Theme changed to:', value)
      document.documentElement.setAttribute('data-theme', value)

      try {
        // Save to API immediately
        await updatePreferences({ theme: value })
        console.log('[Settings] Theme saved successfully')
      } catch (err) {
        console.error('[Settings] Failed to save theme:', err)
      }
    }
  }

  const updateAwsCredential = (index, key, value) => {
    setFormData(prev => {
      const newCreds = [...prev.awsCredentials]
      newCreds[index] = { ...newCreds[index], [key]: value }
      return { ...prev, awsCredentials: newCreds }
    })
  }

  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))
  }

  // User management handlers
  const handleUserClick = (user) => {
    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
    const isOwner = currentUser?.role === 'owner'

    // Can't edit your own role
    if (user.user_id === currentUser?.user_id) {
      return
    }
    // Only owners can edit roles
    if (!isOwner) {
      return
    }
    setEditingUser(user)
    setSelectedRole(user.role)
  }

  const handleSaveRole = async () => {
    if (!editingUser || !selectedRole) return

    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null

    setSavingRole(true)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(
        `${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/users/${editingUser.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: selectedRole })
        }
      )

      if (response.ok) {
        // Update local state
        setAccountUsers(prev => prev.map(u =>
          u.user_id === editingUser.user_id ? { ...u, role: selectedRole } : u
        ))
        setEditingUser(null)
      } else {
        const error = await response.json()
        alert(`Failed to update role: ${error.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to update user role:', err)
      alert(`Failed to update role: ${err.message}`)
    } finally {
      setSavingRole(false)
    }
  }

  // Invitation handlers
  const handleSendInvite = async () => {
    if (!inviteForm.email) {
      alert('Email is required')
      return
    }

    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null

    setSendingInvite(true)
    setInviteLink(null)

    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(
        `${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(inviteForm)
        }
      )

      if (response.ok) {
        const data = await response.json()
        // Show invite link for local testing
        setInviteLink(data.invite_link)
        // Reset form
        setInviteForm({ email: '', name: '', role: 'member' })
        // Reload invitations
        const invitesResponse = await fetch(`${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (invitesResponse.ok) {
          const invitesData = await invitesResponse.json()
          setInvitations(invitesData.invites || [])
        }
      } else {
        const error = await response.json()
        alert(`Failed to send invitation: ${error.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to send invitation:', err)
      alert(`Failed to send invitation: ${err.message}`)
    } finally {
      setSendingInvite(false)
    }
  }

  const handleResendInvite = async (inviteId) => {
    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null

    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(
        `${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites/${inviteId}/resend`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setInviteLink(data.invite_link)
        // Reload invitations
        const invitesResponse = await fetch(`${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (invitesResponse.ok) {
          const invitesData = await invitesResponse.json()
          setInvitations(invitesData.invites || [])
        }
      } else {
        const error = await response.json()
        alert(`Failed to resend invitation: ${error.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to resend invitation:', err)
      alert(`Failed to resend invitation: ${err.message}`)
    }
  }

  const handleCancelInvite = async (inviteId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return

    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null

    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(
        `${getAPIBaseUrl()}/auth/accounts/${currentUser.account_id}/invites/${inviteId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (response.ok) {
        // Update local state
        setInvitations(prev => prev.map(inv =>
          inv.invite_id === inviteId ? { ...inv, status: 'cancelled' } : inv
        ))
      } else {
        const error = await response.json()
        alert(`Failed to cancel invitation: ${error.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to cancel invitation:', err)
      alert(`Failed to cancel invitation: ${err.message}`)
    }
  }

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink)
      alert('Invite link copied to clipboard!')
    }
  }

  // ============================================
  // ACCOUNT REQUEST HANDLERS
  // ============================================

  const handleRequestAccess = async () => {
    if (!selectedAccount) {
      alert('Please select an account')
      return
    }

    setSubmittingRequest(true)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${getAPIBaseUrl()}/accounts/request-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: selectedAccount.account_id,
          message: requestMessage.trim()
        })
      })

      const data = await response.json()

      if (response.ok) {
        alert(`Success! ${data.message}`)
        setSelectedAccount(null)
        setAccountSearchQuery('')
        setRequestMessage('')
        setAccountSearchResults([])

        // Reload requests list
        const requestsResponse = await fetch(`${getAPIBaseUrl()}/auth/me/requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (requestsResponse.ok) {
          const requestsData = await requestsResponse.json()
          setAccountRequests(requestsData.requests || [])
        }
      } else {
        alert(`Failed to request access: ${data.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to request access:', err)
      alert(`Failed to request access: ${err.message}`)
    } finally {
      setSubmittingRequest(false)
    }
  }

  const handleCreateAccount = async () => {
    if (!createForm.name.trim()) {
      alert('Account name is required')
      return
    }

    setCreatingAccount(true)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${getAPIBaseUrl()}/accounts/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: createForm.name.trim(),
          description: createForm.description.trim(),
          s3_bucket: createForm.s3Bucket.trim(),
          s3_region: createForm.s3Region || 'us-east-1'
        })
      })

      const data = await response.json()

      if (response.ok) {
        alert(`Success! ${data.message}\n\nAccount ID: ${data.account.account_id}`)
        setCreateForm({ name: '', description: '', s3Bucket: '', s3Region: 'us-east-1' })

        // Reload user's accounts list
        const accountsResponse = await fetch(`${getAPIBaseUrl()}/auth/me/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (accountsResponse.ok) {
          const accountsData = await accountsResponse.json()
          setMyAccounts(accountsData.accounts || [])
        }
      } else {
        alert(`Failed to create account: ${data.detail || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Failed to create account:', err)
      alert(`Failed to create account: ${err.message}`)
    } finally {
      setCreatingAccount(false)
    }
  }

  const saveSettings = async () => {
    console.log('[Settings] Saving theme:', formData.theme)

    // Save profile (only if we have data)
    if (formData.firstName || formData.lastName || formData.email) {
      await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        language: formData.language,
        dateFormat: formData.dateFormat,
      })
    }

    await updatePreferences({
      theme: formData.theme,
      fontSize: formData.fontSize,
      tooltipLevel: formData.tooltipLevel,
      editorSettings: {
        fontFamily: formData.editorFont,
        minimap: formData.showMinimap,
        lineNumbers: formData.showLineNumbers,
        wordWrap: false, // COBOL/Java code works better without word wrap
        autoSave: formData.autoSave,
      },
    })

    console.log('[Settings] Preferences saved successfully')

    // POC: AWS credentials are administrator-managed — skip save entirely
    // (The PUT /auth/settings endpoint also ignores awsCredentials writes server-side)

    await updateNotifications({
      emailNotifications: formData.emailEnabled,
      jobCompletionAlerts: formData.emailJobComplete,
      errorAlerts: formData.emailJobFailed,
      slackNotifications: formData.slackEnabled,
    })

    // Save file storage settings via IPC (Electron only)
    if (isElectron() && window.electronAPI?.updateFileStorageSettings) {
      await window.electronAPI.updateFileStorageSettings({
        localEnabled: formData.localEnabled,
        localBasePath: formData.localBasePath,
        s3Enabled: formData.s3Enabled,
        s3Bucket: formData.s3Bucket,
        s3Prefix: formData.s3Prefix,
        s3Region: formData.s3Region,
      })
    }

    console.log('[Settings] All settings saved')

    // Apply theme to document after save
    if (formData.theme) {
      console.log('[Settings] Applying theme to document:', formData.theme)
      document.documentElement.setAttribute('data-theme', formData.theme)
    }

    if (onProfileSaved) await onProfileSaved()
  }

  const handleApply = async () => {
    try {
      await saveSettings()
      // Don't close modal - just stay open
    } catch (err) {
      console.error('[Settings] Failed to save settings:', err)
      alert(`Failed to save settings: ${err.message}`)
    }
  }

  const handleSave = async () => {
    try {
      await saveSettings()
      // Close modal after successful save
      onClose()
    } catch (err) {
      console.error('[Settings] Failed to save settings:', err)
      alert(`Failed to save settings: ${err.message}`)
    }
  }

  // System action handlers
  const handleTestApiConnection = async () => {
    setSystemStatus({ loading: true, message: 'Testing API connection...', success: null })
    try {
      const response = await fetch('http://localhost:3001/api/system/test-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: formData.apiBaseUrl })
      })
      const result = await response.json()
      setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
    } catch (error) {
      setSystemStatus({ loading: false, message: error.message, success: false })
    }
  }

  const handleTestDbConnection = async () => {
    setSystemStatus({ loading: true, message: 'Testing database connection...', success: null })
    try {
      const response = await fetch('http://localhost:3001/api/system/db/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: formData.databaseType,
          path: formData.sqlitePath,
          host: formData.sqlHost,
          port: formData.sqlPort,
          database: formData.sqlDatabase,
          username: formData.sqlUsername,
          password: formData.sqlPassword,
          ssl: formData.sqlSslEnabled
        })
      })
      const result = await response.json()
      setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
    } catch (error) {
      setSystemStatus({ loading: false, message: error.message, success: false })
    }
  }

  const handleGenerateDb = async () => {
    setSystemStatus({ loading: true, message: 'Generating database...', success: null })
    try {
      const response = await fetch('http://localhost:3001/api/system/db/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: formData.sqlitePath })
      })
      const result = await response.json()
      setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
    } catch (error) {
      setSystemStatus({ loading: false, message: error.message, success: false })
    }
  }

  const handleResetDb = async () => {
    if (!window.confirm('Are you sure? This will delete ALL data.')) return
    setSystemStatus({ loading: true, message: 'Resetting database...', success: null })
    try {
      const response = await fetch('http://localhost:3001/api/system/db/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: formData.sqlitePath })
      })
      const result = await response.json()
      setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
    } catch (error) {
      setSystemStatus({ loading: false, message: error.message, success: false })
    }
  }

  const handleCleanDb = async () => {
    if (!window.confirm('Are you sure? This will delete all data but keep structure.')) return
    setSystemStatus({ loading: true, message: 'Cleaning database...', success: null })
    try {
      const response = await fetch('http://localhost:3001/api/system/db/clean', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: formData.sqlitePath })
      })
      const result = await response.json()
      setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
    } catch (error) {
      setSystemStatus({ loading: false, message: error.message, success: false })
    }
  }

  const settings = formData

  // Separate standard and custom themes
  const standardThemes = THEMES.filter(t => !t.isCustom)
  const customThemes = THEMES.filter(t => t.isCustom)

  // Render settings sections
  const renderAppearance = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>Theme</h4>
        <div className="theme-grid">
          {standardThemes.map(theme => (
            <div
              key={theme.value}
              className={`theme-card ${settings.theme === theme.value ? 'selected' : ''}`}
              onClick={() => updateSetting('theme', theme.value)}
            >
              <div className={`theme-preview theme-${theme.value}`}>
                <div className="preview-header"></div>
                <div className="preview-sidebar"></div>
                <div className="preview-content">
                  <div className="preview-line"></div>
                  <div className="preview-line short"></div>
                </div>
              </div>
              <div className="theme-info">
                <span className="theme-name">{theme.label}</span>
                <span className="theme-desc">{theme.description}</span>
              </div>
            </div>
          ))}
        </div>

        {customThemes.length > 0 && (
          <>
            <div className="theme-divider">
              <span>Custom Themes</span>
            </div>
            <div className="theme-grid">
              {customThemes.map(theme => (
                <div
                  key={theme.value}
                  className={`theme-card ${settings.theme === theme.value ? 'selected' : ''}`}
                  onClick={() => updateSetting('theme', theme.value)}
                >
                  <div className={`theme-preview theme-${theme.value}`}>
                    <div className="preview-header"></div>
                    <div className="preview-sidebar"></div>
                    <div className="preview-content">
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                    </div>
                  </div>
                  <div className="theme-info">
                    <span className="theme-name">{theme.label}</span>
                    <span className="theme-desc">{theme.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

    </div>
  )

  const renderTooltips = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>Tooltip Level</h4>
        <p className="info-text">
          Choose the level of detail for tooltips throughout the application. Tooltips appear when you hover over nodes, buttons, and other UI elements.
        </p>
        <div className="radio-group">
          <label className="radio-item">
            <input
              type="radio"
              name="tooltipLevel"
              value="off"
              checked={settings.tooltipLevel === 'off'}
              onChange={() => updateSetting('tooltipLevel', 'off')}
            />
            <div className="radio-content">
              <span className="radio-label">Off</span>
              <span className="radio-desc">No tooltips will be displayed</span>
            </div>
          </label>
          <label className="radio-item">
            <input
              type="radio"
              name="tooltipLevel"
              value="simple"
              checked={settings.tooltipLevel === 'simple'}
              onChange={() => updateSetting('tooltipLevel', 'simple')}
            />
            <div className="radio-content">
              <span className="radio-label">Simple</span>
              <span className="radio-desc">Brief descriptions and basic information (recommended for most users)</span>
            </div>
          </label>
          <label className="radio-item">
            <input
              type="radio"
              name="tooltipLevel"
              value="advanced"
              checked={settings.tooltipLevel === 'advanced'}
              onChange={() => updateSetting('tooltipLevel', 'advanced')}
            />
            <div className="radio-content">
              <span className="radio-label">Advanced</span>
              <span className="radio-desc">Technical details, API endpoints, job IDs, and configuration options</span>
            </div>
          </label>
        </div>
      </div>

      <div className="settings-group">
        <h4>What's Included</h4>
        <p className="info-text">Tooltips provide helpful information when hovering over:</p>
        <ul className="feature-list">
          <li><strong>Node Palette</strong> - Description and API endpoint for each node type</li>
          <li><strong>Toolbar Buttons</strong> - Actions like Run, Save, Download, and their keyboard shortcuts</li>
          <li><strong>Canvas Nodes</strong> - Node status, execution results, job IDs, and configuration</li>
          <li><strong>Status Indicators</strong> - What each node state means (running, complete, error, etc.)</li>
        </ul>
      </div>
    </div>
  )

  // Default editor settings
  const DEFAULT_EDITOR_SETTINGS = {
    editorFont: 'JetBrains Mono',
    fontSize: 14,
    showMinimap: true,
    showLineNumbers: true,
    autoSave: true,
  }

  const handleResetEditorDefaults = () => {
    setFormData(prev => ({
      ...prev,
      ...DEFAULT_EDITOR_SETTINGS
    }))
  }

  const renderEditorGeneral = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>Font</h4>
        <div className="form-row">
          <FormField label="Font Family">
            <FormSelect
              value={settings.editorFont}
              onChange={(v) => updateSetting('editorFont', v)}
              options={[
                { value: 'SF Mono', label: 'SF Mono' },
                { value: 'Monaco', label: 'Monaco' },
                { value: 'Fira Code', label: 'Fira Code' },
                { value: 'JetBrains Mono', label: 'JetBrains Mono' },
                { value: 'Cascadia Code', label: 'Cascadia Code' },
              ]}
            />
          </FormField>
          <FormField label="Size">
            <FormSelect
              value={settings.fontSize}
              onChange={(v) => updateSetting('fontSize', parseInt(v))}
              options={[
                { value: 12, label: '12' },
                { value: 13, label: '13' },
                { value: 14, label: '14' },
                { value: 15, label: '15' },
                { value: 16, label: '16' },
                { value: 18, label: '18' },
              ]}
            />
          </FormField>
        </div>
      </div>

      <div className="settings-group">
        <h4>Display</h4>
        <div className="checkbox-list">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={settings.showMinimap}
              onChange={(e) => updateSetting('showMinimap', e.target.checked)}
            />
            <span>Show Minimap</span>
            <span className="checkbox-desc">Display code overview on the right</span>
          </label>
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={settings.showLineNumbers}
              onChange={(e) => updateSetting('showLineNumbers', e.target.checked)}
            />
            <span>Show Line Numbers</span>
            <span className="checkbox-desc">Display line numbers in editor</span>
          </label>
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={settings.autoSave}
              onChange={(e) => updateSetting('autoSave', e.target.checked)}
            />
            <span>Auto Save</span>
            <span className="checkbox-desc">Automatically save changes</span>
          </label>
        </div>
      </div>

      <div className="settings-group">
        <div className="reset-defaults-section">
          <Button variant="secondary" onClick={handleResetEditorDefaults}>
            <RefreshCw size={14} />
            Reset to Defaults
          </Button>
          <span className="reset-hint">Restore default editor settings</span>
        </div>
      </div>
    </div>
  )

  const renderNotifications = () => (
    <div className="settings-panel">
      <div className="settings-group coming-soon-section">
        <div className="section-header-with-badge">
          <h4>Email Notifications</h4>
          <span className="coming-soon-badge">Coming Soon</span>
        </div>
        <div className="coming-soon-content">
          <div className="checkbox-list">
            <label className="checkbox-item disabled">
              <input
                type="checkbox"
                checked={false}
                disabled
              />
              <span>Enable Email Notifications</span>
              <span className="checkbox-desc">Get notified when jobs complete or fail</span>
            </label>
          </div>
          <p className="coming-soon-message">
            Email notifications will be available in a future update. Configure your notification preferences and receive alerts for job completions, failures, and weekly summaries.
          </p>
        </div>
      </div>

      <div className="settings-group coming-soon-section">
        <div className="section-header-with-badge">
          <h4>Slack Notifications</h4>
          <span className="coming-soon-badge">Coming Soon</span>
        </div>
        <div className="coming-soon-content">
          <div className="checkbox-list">
            <label className="checkbox-item disabled">
              <input
                type="checkbox"
                checked={false}
                disabled
              />
              <span>Enable Slack Notifications</span>
              <span className="checkbox-desc">Send notifications to your Slack workspace</span>
            </label>
          </div>
          <p className="coming-soon-message">
            Slack integration will allow you to receive real-time notifications in your team's Slack channels.
          </p>
        </div>
      </div>
    </div>
  )

  const renderFiles = () => {
    const inElectron = isElectron()

    return (
      <div className="settings-panel">
        <p className="info-text">
          Configure local file storage for the desktop application. S3 storage is configured per-account in <strong>Manage Accounts</strong>.
        </p>

        {/* Local Storage - Only available in Electron */}
        <div className="settings-group">
          <div className="section-header-with-badge">
            <h4>Local Storage</h4>
            {!inElectron && <span className="desktop-only-badge">Desktop Only</span>}
          </div>
          {inElectron ? (
            <>
              <div className="checkbox-list">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={settings.localEnabled}
                    onChange={(e) => updateSetting('localEnabled', e.target.checked)}
                  />
                  <span>Enable Local Storage</span>
                  <span className="checkbox-desc">Store files on local filesystem for offline access</span>
                </label>
              </div>
              {settings.localEnabled && (
                <FormField label="Base Path">
                  <FormInput
                    value={settings.localBasePath}
                    onChange={(v) => updateSetting('localBasePath', v)}
                    placeholder="/home/user/modernize/"
                  />
                </FormField>
              )}
              <p className="storage-note">
                Local files are stored at: <code>{settings.localBasePath || '(not set)'}/{'{account_id}/{app_name}/'}</code>
              </p>
            </>
          ) : (
            <p className="portal-note">
              Local storage is only available in the desktop application. The web portal uses S3 for all file storage.
            </p>
          )}
        </div>

        {/* S3 Info Box */}
        <div className="settings-group">
          <div className="section-header-with-badge">
            <h4>S3 Storage</h4>
            <span className="per-account-badge">Per Account</span>
          </div>
          <div className="s3-info-box">
            <Cloud size={20} />
            <div>
              <p><strong>S3 configuration is per-account</strong></p>
              <p>Each account has its own S3 bucket and region. Configure S3 settings in <strong>Manage Accounts</strong> on the landing page.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderConnections = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>Mainframe</h4>
        <FormField label="Host">
          <FormInput
            value={settings.mainframeHost}
            onChange={(v) => updateSetting('mainframeHost', v)}
            placeholder="mainframe.example.com"
          />
        </FormField>
      </div>

      <div className="settings-group">
        <h4>Source Control</h4>
        <FormField label="Git Provider">
          <FormSelect
            value={settings.gitProvider}
            onChange={(v) => updateSetting('gitProvider', v)}
            options={[
              { value: 'github', label: 'GitHub' },
              { value: 'gitlab', label: 'GitLab' },
              { value: 'bitbucket', label: 'Bitbucket' },
              { value: 'azure', label: 'Azure DevOps' },
            ]}
          />
        </FormField>
      </div>
    </div>
  )

  const renderApi = () => (
    <div className="settings-panel">
      {systemStatus.message && (
        <div className={`system-status ${systemStatus.success === true ? 'success' : systemStatus.success === false ? 'error' : 'loading'}`}>
          {systemStatus.loading && <Loader2 size={16} className="spin" />}
          <span>{systemStatus.message}</span>
        </div>
      )}

      <div className="settings-group">
        <h4>API Configuration</h4>
        <FormField label="Base URL">
          <FormInput
            value={settings.apiBaseUrl}
            onChange={(v) => updateSetting('apiBaseUrl', v)}
            placeholder="http://localhost:8000"
          />
        </FormField>
        <p className="info-text">Base URL for the RecodeIT API layer.</p>
        <div className="action-buttons">
          <Button variant="secondary" onClick={handleTestApiConnection} disabled={systemStatus.loading}>
            <PlayCircle size={16} />
            Test Connection
          </Button>
        </div>
      </div>
    </div>
  )

  const renderDatabase = () => {
    const inElectron = isElectron()

    // Database action handlers - use IPC for Electron
    const handleDbAction = async (action) => {
      setSystemStatus({ loading: true, message: `${action === 'test' ? 'Testing' : action === 'generate' ? 'Generating' : action === 'reset' ? 'Resetting' : 'Cleaning'} database...`, success: null })
      try {
        if (inElectron && window.electronAPI?.databaseAction) {
          const result = await window.electronAPI.databaseAction(action, { path: settings.sqlitePath })
          setSystemStatus({ loading: false, message: result.message, success: result.success })
        } else {
          // Fallback for development mode
          const response = await fetch(`http://localhost:3001/api/system/db/${action}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: settings.sqlitePath, type: settings.databaseType })
          })
          const result = await response.json()
          setSystemStatus({ loading: false, message: result.success ? result.message : result.error, success: result.success })
        }
      } catch (error) {
        setSystemStatus({ loading: false, message: error.message, success: false })
      }
    }

    return (
      <div className="settings-panel">
        {/* Desktop Only Notice for Portal */}
        {!inElectron && (
          <div className="portal-database-notice">
            <Database size={20} />
            <div>
              <p><strong>Database is server-configured</strong></p>
              <p>In the web portal, the database connection is managed by the server administrator. SQLite configuration is only available in the desktop application.</p>
            </div>
          </div>
        )}

        {systemStatus.message && (
          <div className={`system-status ${systemStatus.success === true ? 'success' : systemStatus.success === false ? 'error' : 'loading'}`}>
            {systemStatus.loading && <Loader2 size={16} className="spin" />}
            <span>{systemStatus.message}</span>
          </div>
        )}

        {/* SQLite Configuration - Electron only */}
        {inElectron ? (
          <div className="settings-group">
            <div className="section-header-with-badge">
              <h4>SQLite Database</h4>
            </div>
            <FormField label="Database Path">
              <FormInput
                value={settings.sqlitePath}
                onChange={(v) => updateSetting('sqlitePath', v)}
                placeholder="./data/modernizeit.db"
              />
            </FormField>
            <div className="action-buttons">
              <Button variant="secondary" onClick={() => handleDbAction('generate')} disabled={systemStatus.loading}>
                <Database size={16} />
                Generate
              </Button>
              <Button variant="secondary" onClick={() => handleDbAction('reset')} disabled={systemStatus.loading}>
                <RefreshCw size={16} />
                Reset
              </Button>
              <Button variant="secondary" onClick={() => handleDbAction('clean')} disabled={systemStatus.loading}>
                <Eraser size={16} />
                Clean
              </Button>
            </div>
            <p className="info-text">
              <strong>Generate:</strong> Create database schema | <strong>Reset:</strong> Drop & recreate tables | <strong>Clean:</strong> Delete data, keep structure
            </p>
          </div>
        ) : (
          <div className="settings-group">
            <div className="section-header-with-badge">
              <h4>Database</h4>
              <span className="desktop-only-badge">Desktop Only</span>
            </div>
            <p className="portal-note">The desktop application uses SQLite for local data storage.</p>
          </div>
        )}
      </div>
    )
  }

  const renderToolsDownloads = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>RecodeIT Tools</h4>
        <p className="info-text">Download and install RecodeIT tools for local development and deployment.</p>
      </div>

      <div className="settings-group">
        <h4>Docker API Creator</h4>
        <p className="info-text">
          The Docker API Creator is an Electron app that helps you deploy the RecodeIT API locally using Docker containers.
        </p>
        <div className="action-buttons">
          <Button variant="primary" onClick={() => window.open('#', '_blank')}>
            <Download size={16} />
            Download for Mac
          </Button>
          <Button variant="secondary" onClick={() => window.open('#', '_blank')} disabled>
            <Download size={16} />
            Download for Windows (Coming Soon)
          </Button>
        </div>
        <p className="info-text" style={{ marginTop: '10px' }}>
          <strong>Requirements:</strong> Docker Desktop must be installed and running
        </p>
      </div>

      <div className="settings-group">
        <h4>Thick Client</h4>
        <p className="info-text">
          The RecodeIT thick client application for desktop development and transformation workflows.
        </p>
        <div className="action-buttons">
          <Button variant="secondary" onClick={() => window.open('#', '_blank')}>
            <Download size={16} />
            Download Thick Client
          </Button>
        </div>
      </div>
    </div>
  )

  const renderAws = () => {
    // POC: AWS credentials are administrator-managed.
    // We show status metadata only — no secrets, no edit fields, no reveal buttons.
    const cred = awsCredentials || {}
    const configured = cred.configured ?? (cred.accessKeyIdMasked ? true : false)
    const region     = cred.region || 'us-east-1'
    const masked     = cred.accessKeyIdMasked || null

    return (
      <div className="settings-panel">
        <div className="s3-info-box" style={{ marginBottom: '20px' }}>
          <Cloud size={20} />
          <div>
            <p><strong>AWS Bedrock Credentials</strong></p>
            <p>
              Credentials for AI-powered code transformation via Amazon Bedrock are
              managed by your administrator. No credential entry is required.
            </p>
          </div>
        </div>

        <div className="settings-group">
          <h4>Credential Status</h4>
          <div className="credential-block" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                background: configured ? '#3fb950' : '#f85149', flexShrink: 0,
              }} />
              <span style={{ fontSize: 13 }}>
                {configured ? 'Credentials configured' : 'No credentials provisioned — contact your administrator'}
              </span>
            </div>
            {configured && (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: '16px' }}>
                  <span><strong>Region:</strong> {region}</span>
                  {masked && <span><strong>Key ID:</strong> {masked}</span>}
                  <span><strong>Source:</strong> Managed</span>
                </div>
              </>
            )}
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              To update credentials, contact your system administrator.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderUsers = () => {
    // Get current user from localStorage
    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
    const isOwner = currentUser?.role === 'owner'

    const getRoleBadgeClass = (role) => {
      switch(role.toLowerCase()) {
        case 'owner': return 'role-badge role-owner'
        case 'admin': return 'role-badge role-admin'
        case 'member': return 'role-badge role-member'
        default: return 'role-badge'
      }
    }

    return (
      <div className="settings-panel">
        <div className="settings-group">
          <h4>Account Users</h4>
          <p className="info-text">
            Manage users and their access levels for this account. {isOwner ? 'Click on a user to change their role.' : 'Only account owners can change user roles.'}
          </p>

          {loadingUsers ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading users...
            </div>
          ) : (
            <div className="users-grid">
              {accountUsers.map(user => {
                const isCurrentUser = user.user_id === currentUser?.user_id
                const canEdit = isOwner && !isCurrentUser
                return (
                  <div
                    key={user.user_id}
                    className={`user-card ${canEdit ? 'clickable' : ''} ${isCurrentUser ? 'current-user' : ''}`}
                    onClick={() => handleUserClick(user)}
                    style={{ cursor: canEdit ? 'pointer' : 'default' }}
                  >
                    <div className="user-info">
                      <div className="user-avatar">
                        <User size={24} />
                      </div>
                      <div className="user-details">
                        <div className="user-name">
                          {user.name}
                          {isCurrentUser && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>(You)</span>}
                        </div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="user-role">
                      <span className={getRoleBadgeClass(user.role)}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Edit User Role Modal */}
        {editingUser && (
          <Modal isOpen={true} onClose={() => setEditingUser(null)} title="Edit User Role" size="small">
            <div style={{ padding: '20px' }}>
              <div className="user-edit-info" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <div className="user-avatar" style={{ width: '40px', height: '40px' }}>
                    <User size={24} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{editingUser.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{editingUser.email}</div>
                  </div>
                </div>
              </div>

              <FormField label="Role">
                <FormSelect
                  value={selectedRole}
                  onChange={setSelectedRole}
                  options={[
                    { value: 'owner', label: 'Owner - Full access, can manage all users' },
                    { value: 'admin', label: 'Admin - Can manage projects and settings' },
                    { value: 'member', label: 'Member - Can view and edit projects' }
                  ]}
                />
              </FormField>

              <FormActions>
                <Button variant="secondary" onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleSaveRole} disabled={savingRole || selectedRole === editingUser.role}>
                  {savingRole ? 'Saving...' : 'Save'}
                </Button>
              </FormActions>
            </div>
          </Modal>
        )}
      </div>
    )
  }

  const renderInviteUsers = () => {
    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
    const canInvite = currentUser?.role === 'owner' || currentUser?.role === 'admin'

    const getStatusBadge = (status) => {
      const badges = {
        pending: { label: 'Pending', class: 'status-pending' },
        accepted: { label: 'Accepted', class: 'status-accepted' },
        expired: { label: 'Expired', class: 'status-expired' },
        cancelled: { label: 'Cancelled', class: 'status-cancelled' }
      }
      const badge = badges[status] || badges.pending
      return <span className={`status-badge ${badge.class}`}>{badge.label}</span>
    }

    const formatDate = (isoString) => {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    return (
      <div className="settings-panel">
        {/* Invite Form */}
        <div className="settings-group">
          <h4>Send Invitation</h4>
          <p className="info-text">
            Invite new users to join your account. They'll receive an email with a link to create their account.
          </p>

          {!canInvite && (
            <div className="warning-box" style={{ marginTop: '16px' }}>
              <AlertCircle size={16} />
              <span>Only account owners and admins can invite users.</span>
            </div>
          )}

          {canInvite && (
            <div className="invite-form">
              <div className="form-row">
                <FormField label="Email" required>
                  <FormInput
                    type="email"
                    value={inviteForm.email}
                    onChange={(v) => setInviteForm(prev => ({ ...prev, email: v }))}
                    placeholder="email@example.com"
                  />
                </FormField>
                <FormField label="Name (Optional)">
                  <FormInput
                    value={inviteForm.name}
                    onChange={(v) => setInviteForm(prev => ({ ...prev, name: v }))}
                    placeholder="John Doe"
                  />
                </FormField>
              </div>

              <FormField label="Role">
                <FormSelect
                  value={inviteForm.role}
                  onChange={(v) => setInviteForm(prev => ({ ...prev, role: v }))}
                  options={[
                    { value: 'member', label: 'Member - Can view and edit projects' },
                    { value: 'admin', label: 'Admin - Can manage projects and settings' },
                    { value: 'owner', label: 'Owner - Full access, can manage all users' }
                  ]}
                />
              </FormField>

              <Button
                variant="primary"
                onClick={handleSendInvite}
                disabled={sendingInvite || !inviteForm.email}
              >
                <Send size={16} />
                {sendingInvite ? 'Sending...' : 'Send Invitation'}
              </Button>

              {/* Show invite link for local testing */}
              {inviteLink && (
                <div className="invite-link-box">
                  <div className="invite-link-header">
                    <CheckCircle2 size={16} style={{ color: 'var(--success-green)' }} />
                    <strong>Invitation Sent!</strong>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0' }}>
                    For local testing, copy this link:
                  </p>
                  <div className="invite-link-display">
                    <code>{inviteLink}</code>
                    <button className="copy-link-btn" onClick={copyInviteLink} title="Copy link">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending Invitations */}
        <div className="settings-group" style={{ marginTop: '32px' }}>
          <h4>Pending Invitations</h4>
          <p className="info-text">
            Manage invitations that haven't been accepted yet. Invitations expire after 7 days.
          </p>

          {loadingInvites ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading invitations...
            </div>
          ) : invitations.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
              <Mail size={32} style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
              <p style={{ color: 'var(--text-secondary)' }}>No invitations yet</p>
            </div>
          ) : (
            <div className="invitations-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Invited</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(invite => (
                    <tr key={invite.invite_id}>
                      <td>{invite.email}</td>
                      <td>{invite.name || '-'}</td>
                      <td>
                        <span className={`role-badge role-${invite.role}`}>
                          {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                        </span>
                      </td>
                      <td>{getStatusBadge(invite.status)}</td>
                      <td>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {formatDate(invite.invited_at)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {invite.status === 'pending' && formatDate(invite.expires_at)}
                          {invite.status !== 'pending' && '-'}
                        </span>
                      </td>
                      <td>
                        <div className="invite-actions">
                          {invite.status === 'pending' && canInvite && (
                            <>
                              <button
                                className="invite-action-btn"
                                onClick={() => handleResendInvite(invite.invite_id)}
                                title="Resend invitation"
                              >
                                <RefreshCw size={14} />
                              </button>
                              <button
                                className="invite-action-btn danger"
                                onClick={() => handleCancelInvite(invite.invite_id)}
                                title="Cancel invitation"
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}
                          {invite.status !== 'pending' && '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============================================
  // REQUEST ACCOUNT SECTION
  // ============================================

  const renderRequestAccount = () => {
    const getStatusBadge = (status) => {
      const badges = {
        pending: { label: 'Pending', class: 'status-pending' },
        approved: { label: 'Approved', class: 'status-accepted' },
        denied: { label: 'Denied', class: 'status-cancelled' }
      }
      const badge = badges[status] || badges.pending
      return <span className={`status-badge ${badge.class}`}>{badge.label}</span>
    }

    const formatDate = (isoString) => {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    return (
      <div className="settings-panel">
        {/* Join Existing Account Section */}
        <div className="settings-group">
          <h4>Request Access to Account</h4>
          <p className="info-text">
            Search for your organization's account by name and request access.
            Account administrators will review your request.
          </p>

          <div className="request-access-form">
            {!selectedAccount ? (
              <>
                <FormField label="Search Accounts" required>
                  <FormInput
                    value={accountSearchQuery}
                    onChange={(v) => setAccountSearchQuery(v)}
                    placeholder="Type account name to search..."
                  />
                </FormField>

                {/* Search Results */}
                {searchingAccounts && (
                  <div className="account-search-loading">
                    <Loader2 size={16} className="spinning" />
                    <span>Searching...</span>
                  </div>
                )}

                {!searchingAccounts && accountSearchResults.length > 0 && (
                  <div className="account-search-results">
                    <div className="search-results-header">
                      Found {accountSearchResults.length} account{accountSearchResults.length !== 1 ? 's' : ''}
                    </div>
                    {accountSearchResults.map(account => (
                      <div
                        key={account.account_id}
                        className="account-result-card"
                        onClick={() => {
                          setSelectedAccount(account)
                          setAccountSearchQuery('')
                          setAccountSearchResults([])
                        }}
                      >
                        <div className="account-result-header">
                          <Building2 size={18} />
                          <div className="account-result-name">{account.name}</div>
                        </div>
                        {account.description && (
                          <div className="account-result-description">{account.description}</div>
                        )}
                        <div className="account-result-id">{account.account_id}</div>
                      </div>
                    ))}
                  </div>
                )}

                {!searchingAccounts && accountSearchQuery.trim() && accountSearchResults.length === 0 && (
                  <div className="account-search-empty">
                    <AlertCircle size={16} />
                    <span>No accounts found matching "{accountSearchQuery}"</span>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Selected Account Display */}
                <div className="selected-account-display">
                  <div className="selected-account-header">
                    <Building2 size={20} />
                    <div>
                      <div className="selected-account-name">{selectedAccount.name}</div>
                      <div className="selected-account-id">{selectedAccount.account_id}</div>
                    </div>
                    <button
                      className="clear-selection-btn"
                      onClick={() => setSelectedAccount(null)}
                      title="Clear selection"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {selectedAccount.description && (
                    <div className="selected-account-description">{selectedAccount.description}</div>
                  )}
                </div>

                <FormField label="Message (Optional)">
                  <FormTextarea
                    value={requestMessage}
                    onChange={(v) => setRequestMessage(v)}
                    placeholder="Why do you want to join this account?"
                    rows={3}
                  />
                </FormField>

                <Button
                  variant="primary"
                  onClick={handleRequestAccess}
                  disabled={submittingRequest}
                >
                  <Send size={16} />
                  {submittingRequest ? 'Submitting...' : 'Request Access'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Create New Account Section */}
        <div className="settings-group" style={{ marginTop: '32px' }}>
          <h4>Create New Account</h4>
          <p className="info-text">
            Create a new account for your organization. You will be the account owner and can invite other users.
          </p>

          <div className="create-account-form">
            <FormField label="Account Name" required>
              <FormInput
                value={createForm.name}
                onChange={(v) => setCreateForm(prev => ({ ...prev, name: v }))}
                placeholder="My Organization"
              />
            </FormField>

            <FormField label="Description (Optional)">
              <FormTextarea
                value={createForm.description}
                onChange={(v) => setCreateForm(prev => ({ ...prev, description: v }))}
                placeholder="Describe your account's purpose..."
                rows={3}
              />
            </FormField>

            <div className="form-row">
              <FormField label="S3 Bucket (Optional)">
                <FormInput
                  value={createForm.s3Bucket}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, s3Bucket: v }))}
                  placeholder="my-bucket-name"
                />
              </FormField>

              <FormField label="S3 Region">
                <FormSelect
                  value={createForm.s3Region}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, s3Region: v }))}
                  options={[
                    { value: 'us-east-1', label: 'US East (N. Virginia)' },
                    { value: 'us-west-2', label: 'US West (Oregon)' },
                    { value: 'eu-west-1', label: 'EU (Ireland)' },
                    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' }
                  ]}
                />
              </FormField>
            </div>

            <Button
              variant="primary"
              onClick={handleCreateAccount}
              disabled={creatingAccount || !createForm.name.trim()}
            >
              <Plus size={16} />
              {creatingAccount ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </div>

        {/* My Access Requests Section */}
        {loadingRequests ? (
          <div className="settings-group" style={{ marginTop: '32px' }}>
            <h4>My Access Requests</h4>
            <p className="info-text">Loading your requests...</p>
          </div>
        ) : accountRequests.length > 0 && (
          <div className="settings-group" style={{ marginTop: '32px' }}>
            <h4>My Access Requests</h4>
            <p className="info-text" style={{ marginBottom: '16px' }}>
              Track the status of your account access requests below.
            </p>

            <div className="requests-table">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Reviewed</th>
                  </tr>
                </thead>
                <tbody>
                  {accountRequests.map(request => (
                    <tr key={request.request_id}>
                      <td>
                        <div>
                          <div style={{ fontWeight: 500 }}>{request.account_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {request.account_id}
                          </div>
                        </div>
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {request.message || '-'}
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td>{formatDate(request.requested_at)}</td>
                      <td>{request.reviewed_at ? formatDate(request.reviewed_at) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderDefaultAccount = () => {
    const handleDefaultAccountChange = (accountId) => {
      setDefaultAccountId(accountId)
      localStorage.setItem('modernizeit_default_account_id', accountId)
    }

    if (loadingMyAccounts) {
      return (
        <div className="settings-panel">
          <div className="settings-group">
            <h4>Default Account</h4>
            <p className="info-text">Loading your accounts...</p>
          </div>
        </div>
      )
    }

    if (myAccounts.length === 0) {
      return (
        <div className="settings-panel">
          <div className="settings-group">
            <h4>Default Account</h4>
            <div className="warning-box">
              <AlertCircle size={16} />
              <span>No accounts found. Please contact your administrator.</span>
            </div>
          </div>
        </div>
      )
    }

    if (myAccounts.length === 1) {
      return (
        <div className="settings-panel">
          <div className="settings-group">
            <h4>Default Account</h4>
            <p className="info-text">
              You are only a member of one account: <strong>{myAccounts[0].account_name}</strong>
            </p>
            <p className="info-text" style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
              This setting is only relevant if you are a member of multiple accounts.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="settings-panel">
        <div className="settings-group">
          <h4>Default Account</h4>
          <p className="info-text" style={{ marginBottom: '16px' }}>
            You are a member of multiple accounts. Select which account should be used by default when you log in.
          </p>
          <FormField label="Default Account">
            <FormSelect
              value={defaultAccountId || ''}
              onChange={handleDefaultAccountChange}
              options={myAccounts.map(acc => ({
                value: acc.account_id,
                label: `${acc.account_name} (${acc.role})${acc.is_current ? ' - Current' : ''}`
              }))}
            />
          </FormField>
          <p className="info-text" style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
            The default account will be pre-selected when you log in and when creating new projects.
          </p>
        </div>
      </div>
    )
  }

  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================

  const renderShortcuts = () => (
    <div className="settings-panel">
      <p className="info-text">
        Keyboard shortcuts for common actions. These shortcuts follow standard conventions and cannot be customized yet.
      </p>

      {KEYBOARD_SHORTCUTS.map((group) => (
        <div key={group.category} className="settings-group">
          <h4>{group.category}</h4>
          <div className="shortcuts-list">
            {group.shortcuts.map((shortcut) => (
              <div key={shortcut.action} className="shortcut-item">
                <div className="shortcut-keys">
                  {shortcut.keys.split(' + ').map((key, i) => (
                    <span key={i}>
                      {i > 0 && <span className="key-separator">+</span>}
                      <kbd>{key}</kbd>
                    </span>
                  ))}
                </div>
                <div className="shortcut-info">
                  <span className="shortcut-action">{shortcut.action}</span>
                  <span className="shortcut-desc">{shortcut.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  // ============================================
  // ABOUT SECTION
  // ============================================

  const renderAboutApp = () => (
    <div className="settings-panel">
      <div className="about-header">
        <div className="about-logo">
          <GitBranch size={48} />
        </div>
        <div className="about-title">
          <h2>RecodeIT</h2>
          <span className="about-version">Version 1.0.0-beta</span>
        </div>
      </div>

      <div className="settings-group">
        <h4>Application Info</h4>
        <div className="about-info-grid">
          <div className="about-info-item">
            <span className="about-label">Version</span>
            <span className="about-value">1.0.0-beta</span>
          </div>
          <div className="about-info-item">
            <span className="about-label">Build</span>
            <span className="about-value">2025.01.02</span>
          </div>
          <div className="about-info-item">
            <span className="about-label">Platform</span>
            <span className="about-value">{isElectron() ? 'Desktop (Electron)' : 'Web Portal'}</span>
          </div>
          <div className="about-info-item">
            <span className="about-label">License</span>
            <span className="about-value">Commercial</span>
          </div>
        </div>
      </div>

      <div className="settings-group">
        <h4>About</h4>
        <p className="about-description">
          RecodeIT is an AI-powered legacy code transformation platform that helps enterprises
          modernize their applications. Using advanced code analysis and machine learning,
          it intelligently converts legacy codebases into modern languages and frameworks while
          preserving business logic, dependencies, and architectural patterns.
        </p>
      </div>

      <div className="settings-group">
        <h4>Transformation Capabilities</h4>
        <div className="transformation-list">
          <div className="transformation-item">
            <div className="transformation-badge">
              <span className="from-lang">COBOL</span>
              <span className="arrow">→</span>
              <span className="to-lang">Java</span>
            </div>
            <span className="transformation-desc">Mainframe to Enterprise Java</span>
          </div>
          <div className="transformation-item">
            <div className="transformation-badge">
              <span className="from-lang">C++</span>
              <span className="arrow">→</span>
              <span className="to-lang">.NET</span>
            </div>
            <span className="transformation-desc">Legacy C++ to Modern .NET</span>
          </div>
          <div className="transformation-item">
            <div className="transformation-badge">
              <span className="from-lang">VB6</span>
              <span className="arrow">→</span>
              <span className="to-lang">C#</span>
            </div>
            <span className="transformation-desc">Visual Basic to C#</span>
          </div>
          <div className="transformation-item coming-soon">
            <div className="transformation-badge">
              <span className="from-lang">More</span>
              <span className="arrow">→</span>
              <span className="to-lang">Coming Soon</span>
            </div>
            <span className="transformation-desc">Additional transformations in development</span>
          </div>
        </div>
      </div>

      <div className="settings-group">
        <h4>Platform Features</h4>
        <div className="tech-stack">
          <span className="tech-badge feature-badge">AI-Powered</span>
          <span className="tech-badge feature-badge">Dependency Analysis</span>
          <span className="tech-badge feature-badge">Business Logic Preservation</span>
          <span className="tech-badge feature-badge">Multi-Tenant</span>
          <span className="tech-badge feature-badge">Cloud-Native</span>
          <span className="tech-badge feature-badge">Enterprise Security</span>
          <span className="tech-badge feature-badge">Real-Time Collaboration</span>
          <span className="tech-badge feature-badge">Version Control Integration</span>
        </div>
      </div>
    </div>
  )

  const renderHelp = () => (
    <div className="settings-panel">
      <div className="settings-group">
        <h4>Documentation</h4>
        <div className="help-links">
          <a href="#" className="help-link" onClick={(e) => e.preventDefault()}>
            <FileText size={18} />
            <div>
              <span className="help-link-title">User Guide</span>
              <span className="help-link-desc">Learn how to use RecodeIT effectively</span>
            </div>
            <ExternalLink size={14} />
          </a>
          <a href="#" className="help-link" onClick={(e) => e.preventDefault()}>
            <Code size={18} />
            <div>
              <span className="help-link-title">API Documentation</span>
              <span className="help-link-desc">REST API reference for integrations</span>
            </div>
            <ExternalLink size={14} />
          </a>
          <a href="#" className="help-link" onClick={(e) => e.preventDefault()}>
            <Coffee size={18} />
            <div>
              <span className="help-link-title">COBOL-to-Java Guide</span>
              <span className="help-link-desc">Best practices for code transformation</span>
            </div>
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      <div className="settings-group">
        <h4>Support</h4>
        <div className="help-links">
          <a href="#" className="help-link" onClick={(e) => e.preventDefault()}>
            <HelpCircle size={18} />
            <div>
              <span className="help-link-title">Knowledge Base</span>
              <span className="help-link-desc">FAQs and troubleshooting guides</span>
            </div>
            <ExternalLink size={14} />
          </a>
          <a href="#" className="help-link" onClick={(e) => e.preventDefault()}>
            <AlertCircle size={18} />
            <div>
              <span className="help-link-title">Report an Issue</span>
              <span className="help-link-desc">Submit bugs or feature requests</span>
            </div>
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      <div className="settings-group">
        <h4>Contact</h4>
        <p className="info-text">
          For enterprise support, contact your account representative or email support@modernizeit.com
        </p>
      </div>
    </div>
  )

  // Sections that require admin access
  const ADMIN_ONLY_SECTIONS = ['files', 'connections', 'api', 'database']

  // Wrapper for admin-locked content
  const AdminLockedWrapper = ({ children }) => {
    if (isAdminUnlocked) {
      return (
        <div className="admin-unlocked-section">
          <div className="admin-section-badge">
            <Shield size={12} />
            <span>Admin Access</span>
          </div>
          {children}
        </div>
      )
    }
    return (
      <div className="admin-locked-overlay">
        <div className="locked-content-blur">
          {children}
        </div>
        <div className="locked-message">
          <div className="locked-icon-container">
            <Lock size={32} />
          </div>
          <h3>Admin Access Required</h3>
          <p>These settings are restricted to administrators.</p>
          <button className="unlock-prompt-btn" onClick={() => setShowLoginPanel(true)}>
            <Unlock size={16} />
            <span>Unlock Settings</span>
          </button>
        </div>
      </div>
    )
  }

  const renderTargetProfiles = () => {
    const currentUserStr = localStorage.getItem('modernizeit_user')
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
    const accountId = currentUser?.account_id ?? null
    return (
      <div className="settings-panel">
        <h3 className="settings-panel-title">Target Profiles</h3>
        <p className="settings-panel-description">
          Reusable target environment profiles — store your language, build tool, and framework
          settings once, then reuse across Workflow and Workspace.
        </p>
        <div style={{ marginTop: '1.5rem' }}>
          <Button variant="primary" onClick={() => setShowTargetProfilesModal(true)}>
            <Layers size={16} style={{ marginRight: '0.5rem' }} />
            Manage Target Profiles
          </Button>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    const content = (() => {
      switch (activeSection) {
        case 'appearance': return renderAppearance()
        case 'editor-general': return renderEditorGeneral()
        case 'notifications': return renderNotifications()
        case 'shortcuts': return renderShortcuts()
        case 'tooltips': return renderTooltips()
        case 'files': return renderFiles()
        case 'connections': return renderConnections()
        case 'api': return renderApi()
        case 'database': return renderDatabase()
        case 'tools-downloads': return renderToolsDownloads()
        case 'target-profiles': return renderTargetProfiles()
        case 'aws': return renderAws()
        case 'users': return renderUsers()
        case 'invite-users': return renderInviteUsers()
        case 'request-account': return renderRequestAccount()
        case 'default-account': return renderDefaultAccount()
        case 'about-app': return renderAboutApp()
        case 'help': return renderHelp()
        default: return <div className="settings-panel"><p>Select a setting from the left.</p></div>
      }
    })()

    // Wrap admin-only sections
    if (ADMIN_ONLY_SECTIONS.includes(activeSection)) {
      return <AdminLockedWrapper>{content}</AdminLockedWrapper>
    }

    return content
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Settings" size="xlarge">
      <div className="settings-layout">
        {/* Left Sidebar */}
        <div className="settings-sidebar">
          <div className="settings-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
          </div>
          <div className="settings-tree">
            {filteredTree.map(category => {
              const CategoryIcon = category.icon
              const isExpanded = expandedCategories.includes(category.id)
              return (
                <div key={category.id} className="tree-category">
                  <button
                    className="tree-category-header"
                    onClick={() => toggleCategory(category.id)}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <CategoryIcon size={16} />
                    <span>{category.label}</span>
                  </button>
                  {isExpanded && category.children && (
                    <div className="tree-children">
                      {category.children.map(child => {
                        const ChildIcon = child.icon
                        return (
                          <button
                            key={child.id}
                            className={`tree-item ${activeSection === child.id ? 'active' : ''} ${child.disabled ? 'disabled' : ''}`}
                            onClick={() => !child.disabled && setActiveSection(child.id)}
                            disabled={child.disabled}
                            title={child.disabled ? 'Coming soon' : ''}
                          >
                            <ChildIcon size={14} />
                            <span>{child.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Right Content */}
        <div className="settings-main">
          <div className="settings-breadcrumb">
            <div className="breadcrumb-left">
              {breadcrumb.map((item, index) => (
                <span key={index}>
                  {index > 0 && <ChevronRight size={14} />}
                  <span className={index === breadcrumb.length - 1 ? 'current' : ''}>{item}</span>
                </span>
              ))}
            </div>

            {/* Admin Lock */}
            <div className="admin-lock-container" ref={loginPanelRef}>
              <button
                className={`admin-lock-btn ${isAdminUnlocked ? 'unlocked' : ''}`}
                onClick={handleLockClick}
                title={isAdminUnlocked ? 'Admin Mode Active - Click to Lock' : 'Unlock Admin Settings'}
              >
                <div className="lock-icon-wrapper">
                  {isAdminUnlocked ? (
                    <Unlock size={16} className="lock-icon unlocked" />
                  ) : (
                    <Lock size={16} className="lock-icon" />
                  )}
                </div>
                <span className="lock-label">
                  {isAdminUnlocked ? 'Admin' : 'Locked'}
                </span>
                {isAdminUnlocked && <div className="admin-pulse" />}
              </button>

              {/* Login Panel */}
              {showLoginPanel && !isAdminUnlocked && (
                <div className={`admin-login-panel ${loginError ? 'shake' : ''} ${loginSuccess ? 'success' : ''}`}>
                  <div className="login-panel-header">
                    <Shield size={20} />
                    <span>Admin Authentication</span>
                  </div>
                  <form onSubmit={handleAdminLogin}>
                    <div className="login-field">
                      <label>Username</label>
                      <input
                        ref={usernameInputRef}
                        type="text"
                        value={adminUsername}
                        onChange={(e) => setAdminUsername(e.target.value)}
                        placeholder="Enter username"
                        autoComplete="off"
                      />
                    </div>
                    <div className="login-field">
                      <label>Password</label>
                      <div className="login-password-wrapper">
                        <input
                          type={showAdminPassword ? 'text' : 'password'}
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          placeholder="Enter password"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="login-eye-btn"
                          onClick={() => setShowAdminPassword(!showAdminPassword)}
                        >
                          {showAdminPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    {loginError && (
                      <div className="login-error">
                        <AlertCircle size={14} />
                        <span>{loginError}</span>
                      </div>
                    )}
                    {loginSuccess && (
                      <div className="login-success">
                        <CheckCircle2 size={14} />
                        <span>Access Granted</span>
                      </div>
                    )}
                    <button type="submit" className="login-submit-btn" disabled={loginSuccess}>
                      {loginSuccess ? (
                        <>
                          <CheckCircle2 size={16} />
                          <span>Unlocked</span>
                        </>
                      ) : (
                        <>
                          <Unlock size={16} />
                          <span>Unlock</span>
                        </>
                      )}
                    </button>
                  </form>
                  <div className="login-panel-footer">
                    Press Enter to submit
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="settings-content">
            {renderContent()}
          </div>
        </div>
      </div>

      <FormActions>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="secondary" onClick={handleApply}>Apply</Button>
        <Button variant="primary" onClick={handleSave}>OK</Button>
      </FormActions>

      {/* Target Profiles modal — triggered from the Target Profiles settings section */}
      {(() => {
        const currentUserStr = localStorage.getItem('modernizeit_user')
        const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null
        return (
          <TargetProfilesModal
            isOpen={showTargetProfilesModal}
            onClose={() => setShowTargetProfilesModal(false)}
            accountId={currentUser?.account_id ?? null}
          />
        )
      })()}
    </Modal>
  )
}

export default SettingsModal
